import { useCallback, useEffect, useRef, useState } from 'react';

import {
  AlignLeftOutlined,
  EyeOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { GetProp, MenuProps, TableProps } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { Company } from 'models/Company';
import { Country } from 'models/Country';

import { CompanyService } from 'services/CompanyService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import { CompanyModalHandlers, PAGE_SIZE, TableParams } from '../types';

interface UseCompanyTableParams {
  message: MessageInstance;
  setTableLoading: (loading: boolean) => void;
  modalHandlers: CompanyModalHandlers;
}

export interface UseCompanyTableReturn {
  filteredDataSource: Company[];
  tableParams: TableParams;
  setTableParams: React.Dispatch<React.SetStateAction<TableParams>>;
  onSearch: (value: string) => void;
  handleTableChange: TableProps<Company>['onChange'];
  getDropdownMenu: (row: Company) => MenuProps['items'];
  fetchDataFromServer: (withLoading?: boolean) => Promise<void>;
  findCountryByCode: (
    code: string,
    countries: Country[]
  ) => Country | undefined;
}

export function useCompanyTable({
  message,
  setTableLoading,
  modalHandlers,
}: UseCompanyTableParams): UseCompanyTableReturn {
  const { t } = useTranslation();

  // useRef: fuente de verdad completa — no dispara renders por sí sola
  const dataSourceRef = useRef<Company[]>([]);

  const [filteredDataSource, setFilteredDataSource] = useState<Company[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: { current: 1, pageSize: PAGE_SIZE },
  });
  const [term, setTerm] = useState('');

  useEffect(() => {
    fetchDataFromServer(false);
  }, []);

  useEffect(() => {
    applyFilter(tableParams.pagination?.current);
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams.sortField,
    tableParams.sortOrder,
  ]);

  useEffect(() => {
    applyFilter(1);
  }, [term]);

  const fetchDataFromServer = async (withLoading = false) => {
    if (withLoading) {
      setTableLoading(true);
    }

    const response = await CompanyService.getAll();

    if (response.success) {
      dataSourceRef.current = response.data as Company[];
      applyFilter(1, dataSourceRef.current);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.settings.companies.scene.errors.fetch_companies'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const applyFilter = useCallback(
    (currentPage = 1, data?: Company[]) => {
      const termFilter = FunctionsHelper.normalizeText(term.trim());
      let filteredData =
        data !== undefined ? [...data] : [...dataSourceRef.current];

      filteredData = filteredData.sort((a, b) => b.id - a.id);

      if (termFilter.length > 0) {
        filteredData = filteredData.filter((record) =>
          FunctionsHelper.normalizeText(record.name).includes(termFilter)
        );
      }

      const pageSize = tableParams.pagination?.pageSize || PAGE_SIZE;
      const total = filteredData.length;
      const startIndex = (currentPage - 1) * pageSize;
      const paginatedData = filteredData.slice(
        startIndex,
        startIndex + pageSize
      );

      setFilteredDataSource(paginatedData);
      setTableParams((prev) => ({
        ...prev,
        pagination: { ...prev.pagination, current: currentPage, total },
      }));
    },
    [term, tableParams.pagination?.pageSize]
  );

  const onSearch = useCallback((value: string) => {
    setTerm(value);
  }, []);

  const handleTableChange: TableProps<Company>['onChange'] = useCallback(
    (
      pagination: TableParams['pagination'],
      filters: Parameters<GetProp<TableProps<Company>, 'onChange'>>[1],
      sorter: Parameters<GetProp<TableProps<Company>, 'onChange'>>[2]
    ) => {
      const s = sorter as any;

      setTableParams((prev) => {
        const next: TableParams = {
          pagination,
          filters,
          sortOrder: s.order && s.columnKey ? s.order : undefined,
          sortField: s.order && s.columnKey ? s.columnKey : undefined,
        };

        if (pagination?.pageSize !== prev.pagination?.pageSize) {
          dataSourceRef.current = [];
          setFilteredDataSource([]);
        }

        return next;
      });
    },
    []
  );

  const getDropdownMenu = useCallback(
    (row: Company): MenuProps['items'] => {
      const { openDetailModal, openMenuDetailModal, openChangePasswordModal } =
        modalHandlers;

      return [
        {
          key: '1',
          label: (
            <span>
              <EyeOutlined style={{ marginRight: '8px' }} />
              {t('scenes.settings.companies.scene.dropdown.view_detail')}
            </span>
          ),
          onClick: () => openDetailModal(row),
        },
        {
          key: '2',
          label: (
            <span>
              <AlignLeftOutlined style={{ marginRight: '8px' }} />
              {t('scenes.settings.companies.scene.dropdown.menus')}
            </span>
          ),
          onClick: () => openMenuDetailModal(row),
        },
        { type: 'divider' as const },
        {
          type: 'group' as const,
          label: t(
            'scenes.settings.companies.components.change_password_modal.labels.encrypted_reports'
          ),
        },
        {
          key: '3',
          label: (
            <span>
              <LockOutlined style={{ marginRight: '8px' }} />
              {t('scenes.settings.companies.scene.dropdown.change_password')}
            </span>
          ),
          onClick: () => openChangePasswordModal(row),
        },
      ];
    },
    [modalHandlers, t]
  );

  const findCountryByCode = useCallback(
    (code: string, countries: Country[]) =>
      countries.find((c) => c.code === code),
    []
  );

  return {
    filteredDataSource,
    tableParams,
    setTableParams,
    onSearch,
    handleTableChange,
    getDropdownMenu,
    fetchDataFromServer,
    findCountryByCode,
  };
}
